import React, { useState, useEffect, createContext, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Firebase
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Loader from "./components/Loader";
import BackToTop from "./components/BackToTop";
import NotFound from "./components/NotFound";

// Pages
import Home from "./pages/Home";
import Updates from "./pages/Updates";
import Stats from "./pages/Stats";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Gallery from "./pages/Gallery";
import Vote from "./pages/Vote";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Auth pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";

// Forms (Protected)
import StaffApply from "./forms/StaffApply";
import PlayerReport from "./forms/PlayerReport";
import BugReport from "./forms/BugReport";
import UnbanRequest from "./forms/UnbanRequest";
import PurchaseSupport from "./forms/PurchaseSupport";
import CreatorApply from "./forms/CreatorApply";
import GeneralSupport from "./forms/GeneralSupport";
import BanReport from "./forms/BanReport";
import RuleBreakReport from "./forms/RuleBreakReport";
import SuggestionForm from "./forms/SuggestionForm";
import EventFeedback from "./forms/EventFeedback";
import AppealMinorPunishment from "./forms/AppealMinorPunishment";
import PartnershipApplication from "./forms/PartnershipApplication";
import MediaInfluencerApply from "./forms/MediaInfluencerApply";
import BuilderApplication from "./forms/BuilderApplication";
import ModeratorApplication from "./forms/ModeratorApplication";
import WhitelistRequest from "./forms/WhitelistRequest";
import CustomRankRequest from "./forms/CustomRankRequest";
import NameChangeRequest from "./forms/NameChangeRequest";

// Auth context
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LoadingHandler = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [location]);

  if (loading) return <Loader />;
  return children;
};

const ProtectedRoute = ({ element }) => {
  const { user } = useAuth();
  return user ? element : <Navigate to="/login" replace />;
};

function App() {
  const [user, setUser] = useState(null);

  // Firebase auth state tracking
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user }}>
      <Router>
        <div className="app">
          <Navbar user={user} />
          <ScrollToTop />

          <LoadingHandler>
            <div className="main-content">
              <Routes>
                {/* Public Pages */}
                <Route path="/" element={<Home />} />
                <Route path="/updates" element={<Updates />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/vote" element={<Vote />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />

                {/* Auth Pages */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Protected Forms */}
                <Route path="/forms/staff" element={<ProtectedRoute element={<StaffApply />} />} />
                <Route path="/forms/player-report" element={<ProtectedRoute element={<PlayerReport />} />} />
                <Route path="/forms/bug-report" element={<ProtectedRoute element={<BugReport />} />} />
                <Route path="/forms/creator-apply" element={<ProtectedRoute element={<CreatorApply />} />} />
                <Route path="/forms/purchase-support" element={<ProtectedRoute element={<PurchaseSupport />} />} />
                <Route path="/forms/unban-request" element={<ProtectedRoute element={<UnbanRequest />} />} />
                <Route path="/forms/general-support" element={<ProtectedRoute element={<GeneralSupport />} />} />
                <Route path="/forms/ban-report" element={<ProtectedRoute element={<BanReport />} />} />
                <Route path="/forms/rule-break-report" element={<ProtectedRoute element={<RuleBreakReport />} />} />
                <Route path="/forms/suggestion" element={<ProtectedRoute element={<SuggestionForm />} />} />
                <Route path="/forms/event-feedback" element={<ProtectedRoute element={<EventFeedback />} />} />
                <Route path="/forms/appeal-minor-punishment" element={<ProtectedRoute element={<AppealMinorPunishment />} />} />
                <Route path="/forms/partnership-application" element={<ProtectedRoute element={<PartnershipApplication />} />} />
                <Route path="/forms/media-influencer-apply" element={<ProtectedRoute element={<MediaInfluencerApply />} />} />
                <Route path="/forms/builder-application" element={<ProtectedRoute element={<BuilderApplication />} />} />
                <Route path="/forms/moderator-application" element={<ProtectedRoute element={<ModeratorApplication />} />} />
                <Route path="/forms/whitelist-request" element={<ProtectedRoute element={<WhitelistRequest />} />} />
                <Route path="/forms/custom-rank-request" element={<ProtectedRoute element={<CustomRankRequest />} />} />
                <Route path="/forms/name-change-request" element={<ProtectedRoute element={<NameChangeRequest />} />} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>

              <BackToTop />
            </div>
          </LoadingHandler>

          <Footer />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
